import { useState, useEffect } from 'react';
import AlertModal from './components/AlertModal';

// ─── Field Definitions ────────────────────────────────────────────────────────

const CUSTOMER_FIELDS = [
  { id: 'partyname',    label: 'Registered Business Name', type: 'text',  placeholder: 'Registered business name',         required: true  },
  { id: 'tin',          label: 'TIN',                      type: 'text',  placeholder: 'Tax Identification Number',          required: true  },
  { id: 'email',        label: 'Email Address',            type: 'email', placeholder: 'customer@example.com',               required: true  },
  { id: 'telephone',    label: 'Telephone',                type: 'tel',   placeholder: '+234 000 000 0000',                  required: true  },
  { id: 'businessdesc', label: 'Business Description',     type: 'text',  placeholder: 'Brief description of the business',  required: false },
  { id: 'streetname',   label: 'Street Name',              type: 'text',  placeholder: '12 Commerce Road',                   required: true  },
  { id: 'cityname',     label: 'City Name',                type: 'text',  placeholder: 'Lagos',                              required: true  },
  { id: 'postalzone',   label: 'Postal Zone',              type: 'text',  placeholder: '100001',                             required: false },
  { id: 'country',      label: 'Country',                  type: 'text',  placeholder: 'Nigeria',                            required: true  },
];

// ─── Initial State ─────────────────────────────────────────────────────────────

const buildInitialState = (fields) =>
  fields.reduce((acc, f) => ({ ...acc, [f.id]: '' }), {});

// ─── Reusable Field Component ─────────────────────────────────────────────────

function FormField({ field, value, onChange }) {
  const baseClass =
    'w-full px-4 py-2.5 bg-gray-900/70 text-white rounded-lg border border-gray-600 ' +
    'focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 focus:outline-none ' +
    'transition-all duration-200 placeholder-gray-500 text-sm';

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={field.id} className="text-sm font-medium text-gray-300 flex items-center gap-1">
        {field.label}
        {field.required && <span className="text-red-400 text-xs">*</span>}
      </label>

      {field.type === 'textarea' ? (
        <textarea
          id={field.id}
          value={value}
          onChange={onChange}
          rows={field.rows || 4}
          required={field.required}
          placeholder={field.placeholder}
          className={`${baseClass} resize-none font-mono text-xs`}
        />
      ) : (
        <input
          id={field.id}
          type={field.type}
          value={value}
          onChange={onChange}
          required={field.required}
          placeholder={field.placeholder}
          className={baseClass}
        />
      )}
    </div>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────

function SectionCard({ icon, title, subtitle, accent, children }) {
  return (
    <div className={`rounded-xl border ${accent} bg-gray-800/50 backdrop-blur-sm overflow-hidden`}>
      {/* Card Header */}
      <div className="px-6 py-4 border-b border-gray-700/60 flex items-center gap-3">
        <div className="text-2xl">{icon}</div>
        <div>
          <h3 className="text-white font-semibold text-base leading-tight">{title}</h3>
          <p className="text-gray-400 text-xs mt-0.5">{subtitle}</p>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * CustomerForm — Load and save FIRS custom field data for a Manager.io customer.
 *
 * Props:
 *   customerId  {string}    required — Manager.io customer UUID
 *   onSaved     {function}  optional — called after a successful save
 *   onCancel    {function}  optional — called when the Cancel button is clicked
 */
function CustomerForm({ customerId, onSaved, onCancel }) {
  const [customerData, setCustomerData] = useState(buildInitialState(CUSTOMER_FIELDS));
  const [loading, setLoading]           = useState(false);
  const [dataLoaded, setDataLoaded]     = useState(false);

  const [alert, setAlert] = useState({
    isOpen:  false,
    type:    'info',
    title:   '',
    message: '',
  });

  // ── Load existing data on mount / when customerId changes ──────────────────

  useEffect(() => {
    if (!customerId) return;

    (async () => {
      setLoading(true);
      setDataLoaded(false);
      try {
        const { fnGetCustomerData } = await import('./serverjs/customerFuncs');
        const existing = await fnGetCustomerData(customerId);

        if (existing) {
          setCustomerData({
            partyname:    existing.partyname,
            tin:          existing.tin,
            email:        existing.email,
            telephone:    existing.telephone,
            businessdesc: existing.businessdesc,
            streetname:   existing.streetname,
            cityname:     existing.cityname,
            postalzone:   existing.postalzone,
            country:      existing.country,
          });
        }
        setDataLoaded(true);
      } catch (err) {
        console.error('Error loading customer data:', err);
        showAlert('Failed to load customer data. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [customerId]);

  // ── Alert Helpers ─────────────────────────────────────────────────────────

  const showAlert = (message, type = 'info', title = '') => {
    const titles = {
      success: '✅ Success',
      error:   '❌ Error',
      warning: '⚠️ Warning',
      info:    'ℹ️ Information',
    };
    setAlert({ isOpen: true, type, title: title || titles[type], message });
  };

  const closeAlert = () => setAlert((prev) => ({ ...prev, isOpen: false }));

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { id, value } = e.target;
    setCustomerData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerId) {
      showAlert('No customer selected. Please provide a Customer ID.', 'warning');
      return;
    }
    setLoading(true);
    try {
      const { fnPutCustomerData } = await import('./serverjs/customerFuncs');
      const result = await fnPutCustomerData(customerId, customerData);

      if (result === true) {
        showAlert('Customer details saved successfully.', 'success');
        onSaved?.();
      } else {
        showAlert('Failed to save customer details. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Save error:', error);
      showAlert('An error occurred while saving. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Guard — no customerId provided ────────────────────────────────────────

  if (!customerId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-slate-900 flex items-center justify-center px-4">
        <div className="rounded-xl border border-red-500/30 bg-gray-800/50 backdrop-blur-sm p-8 max-w-md w-full text-center space-y-3">
          <div className="text-4xl">⚠️</div>
          <h2 className="text-white font-semibold text-lg">No Customer Selected</h2>
          <p className="text-gray-400 text-sm">
            A valid Customer ID is required to load or save customer details.
          </p>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-slate-900 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* ── Page Header ── */}
        <div className="text-center space-y-1 mb-2">
          <h1 className="text-3xl font-bold text-white tracking-tight">
            👤&nbsp; Customer Details
          </h1>
          <p className="text-gray-400 text-sm">
            FIRS-registered information for this customer
          </p>
          {/* Customer ID badge */}
          <p className="text-gray-600 text-xs font-mono mt-1">
            ID: {customerId}
          </p>
        </div>

        {/* ── Loading skeleton ── */}
        {loading && !dataLoaded && (
          <div className="rounded-xl border border-gray-700 bg-gray-800/50 backdrop-blur-sm p-8 flex items-center justify-center gap-3 text-gray-400 text-sm">
            <span className="animate-spin text-xl">⏳</span>
            Loading customer data…
          </div>
        )}

        {/* ── Form ── */}
        {(!loading || dataLoaded) && (
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>

            <SectionCard
              icon="🏢"
              title="Customer Information"
              subtitle="FIRS-registered details for invoicing and compliance"
              accent="border-cyan-500/30"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {CUSTOMER_FIELDS.map((field) => (
                  <div
                    key={field.id}
                    className={field.id === 'businessdesc' ? 'sm:col-span-2' : ''}
                  >
                    <FormField
                      field={field}
                      value={customerData[field.id]}
                      onChange={handleChange}
                    />
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ── Actions ── */}
            <div className="flex items-center justify-end gap-3 flex-wrap">
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600
                             disabled:bg-gray-800 disabled:cursor-not-allowed text-white font-semibold
                             rounded-lg shadow-lg transition-all duration-200 hover:scale-105
                             disabled:hover:scale-100 text-sm border border-gray-600"
                >
                  ✖ Cancel
                </button>
              )}

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-8 py-3 bg-emerald-600 hover:bg-emerald-500
                           disabled:bg-emerald-900 disabled:cursor-not-allowed text-white font-semibold
                           rounded-lg shadow-lg transition-all duration-200 hover:scale-105
                           disabled:hover:scale-100 text-sm"
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⏳</span> Saving…
                  </>
                ) : (
                  <>💾 Save Customer</>
                )}
              </button>
            </div>
          </form>
        )}

      </div>

      <AlertModal
        isOpen={alert.isOpen}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={closeAlert}
      />
    </div>
  );
}

export default CustomerForm;
